/**
 * useRoleOptions — Reactive role dropdown options sourced from the /roles API.
 *
 * Apps provide dynamic role options fetched from the `/roles` API by calling
 * `provideRoleOptions(optionsRef)` near the application root. Components
 * consuming `useRoleOptions()` receive the API-sourced list once available,
 * and an empty array until then — there is no static fallback.
 *
 * `useRoleLabelGetter` retains a static i18n map for display-only contexts
 * (CSV preview, read-only labels).
 */
import { computed, inject, provide, type ComputedRef, type InjectionKey, type Ref } from 'vue';
import { UserRoleCodeSchema, type UserRoleCode } from './types';
import { useSharedTranslation } from '../../composables/useTranslationProvider';

// ── Provide/inject for API-sourced role options ────────────────────────────

/** Shape of a single role option for dropdowns. */
export type RoleOption = { value: UserRoleCode; label: string };

/** Injection key for dynamically-provided role options. */
export const RoleOptionsKey: InjectionKey<Ref<RoleOption[]> | ComputedRef<RoleOption[]>> =
  Symbol('RoleOptions');

/**
 * Provide API-fetched role options to all descendant components.
 * Call once near the application root (e.g. `App.vue`).
 *
 * Pass a reactive ref that starts empty and is populated after the API call;
 * shared components will automatically update when the ref's value changes.
 */
export function provideRoleOptions(options: Ref<RoleOption[]> | ComputedRef<RoleOption[]>): void {
  provide(RoleOptionsKey, options);
}

// ── i18n fallback map ──────────────────────────────────────────────────────

/** Role code → i18n key + English default label. */
const ROLE_I18N: Record<string, { key: string; fallback: string }> = {
  TECHNICAL_ADMIN: { key: 'user.roleTechnicalAdmin', fallback: 'Technical Admin' },
  CLINICAL_ADMIN: { key: 'user.roleClinicalAdmin', fallback: 'Clinical Admin' },
  NICU_MANAGER: { key: 'user.roleNicuManager', fallback: 'NICU Manager' },
  NURSE: { key: 'user.roleNurse', fallback: 'Nurse' },
  PREP_TECHNICIAN: { key: 'user.rolePrepTechnician', fallback: 'Prep Technician' },
};

/**
 * Pre-built Set for O(1) membership checks against the 5 UI-supported role
 * codes (`UserRoleCodeSchema`). Consistent with `RoleOption.value` typing.
 */
export const ALLOWED_ROLE_CODES: ReadonlySet<string> = new Set(UserRoleCodeSchema.options);

/**
 * Returns a reactive computed array of `{ value, label }` role options
 * suitable for PrimeVue Select / MultiSelect dropdowns.
 *
 * Returns API-sourced options when a provider has populated them via
 * `provideRoleOptions()`. Returns an empty array until API data arrives —
 * there is no static fallback.
 */
export function useRoleOptions(): ComputedRef<{ value: UserRoleCode; label: string }[]> {
  const injected = inject(RoleOptionsKey, null);

  return computed(() => {
    // Use API-sourced options only; empty until the API response arrives
    if (injected && injected.value.length > 0) return injected.value;
    return [];
  });
}

/**
 * Translate a single role code to its localized label.
 * Intended for display contexts (CSV preview, read-only views).
 */
export function useRoleLabelGetter(): (code: string) => string {
  const { t, locale } = useSharedTranslation();

  return (code: string): string => {
    const _lng = locale.value; // reactivity trigger
    const meta = ROLE_I18N[code];
    if (!meta) return code;
    return t(meta.key, meta.fallback);
  };
}
